import { resolveApiUrl } from "./apiUrl";
import { clientFingerprintHeaders } from "./clientFingerprint";
import { nativeAppRequestHeaders } from "./nativeApp";

const pendingTouchKey = "mmp_attribution_touch";
const pendingTouchMaxAge = 30 * 24 * 60 * 60 * 1000;

interface AttributionTouchPayload {
  shareCode?: string;
  provider?: string;
  mediaSource?: string;
  campaignId?: string;
  campaign?: string;
  adSetId?: string;
  adSet?: string;
  adId?: string;
  ad?: string;
  clickId?: string;
  landingUrl?: string;
  referrer?: string;
}

interface PendingAttributionTouch {
  capturedAt: number;
  payload: AttributionTouchPayload;
}

function firstParam(params: URLSearchParams, names: string[]): string {
  for (const name of names) {
    const value = (params.get(name) || "").trim();
    if (value) return value;
  }
  return "";
}

function detectProvider(params: URLSearchParams): string {
  const explicit = firstParam(params, ["mmp_provider", "attribution_provider"]);
  if (explicit) return explicit.toLowerCase();
  if (["af_click_id", "af_c_id", "af_ad_id", "pid"].some((key) => params.has(key))) return "appsflyer";
  if (["adj_t", "adjust_t", "adjust_campaign"].some((key) => params.has(key))) return "adjust";
  if (["branch_match_id", "~campaign", "~channel"].some((key) => params.has(key))) return "branch";
  return "";
}

function touchFromLocation(): AttributionTouchPayload | null {
  const params = new URLSearchParams(window.location.search);
  const payload: AttributionTouchPayload = {
    shareCode: firstParam(params, ["ref", "shareCode"]).toUpperCase(),
    provider: detectProvider(params),
    mediaSource: firstParam(params, ["media_source", "pid", "utm_source", "mmp_source", "~channel"]),
    campaignId: firstParam(params, ["campaign_id", "af_c_id", "campaignid", "utm_id"]),
    campaign: firstParam(params, ["campaign", "c", "utm_campaign", "~campaign", "adjust_campaign"]),
    adSetId: firstParam(params, ["adset_id", "af_adset_id", "adgroupid"]),
    adSet: firstParam(params, ["adset", "af_adset", "utm_term"]),
    adId: firstParam(params, ["ad_id", "af_ad_id", "creative_id"]),
    ad: firstParam(params, ["ad", "af_ad", "utm_content"]),
    clickId: firstParam(params, ["click_id", "af_click_id", "adj_t", "gclid", "fbclid", "ttclid", "branch_match_id"]),
    landingUrl: window.location.href,
    referrer: document.referrer,
  };
  const hasAttribution = Object.entries(payload).some(([key, value]) => !["landingUrl", "referrer"].includes(key) && Boolean(value));
  return hasAttribution ? payload : null;
}

function readPendingTouch(): PendingAttributionTouch | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(pendingTouchKey) || "null") as PendingAttributionTouch | null;
    if (!parsed?.capturedAt || !parsed.payload || Date.now() - parsed.capturedAt > pendingTouchMaxAge) {
      window.localStorage.removeItem(pendingTouchKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storePendingTouch(touch: PendingAttributionTouch): void {
  try {
    window.localStorage.setItem(pendingTouchKey, JSON.stringify(touch));
  } catch {
    // The immediate request still works when local storage is unavailable.
  }
}

export async function captureAttributionTouch(): Promise<void> {
  if (typeof window === "undefined") return;
  const currentPayload = touchFromLocation();
  const pending = currentPayload ? { capturedAt: Date.now(), payload: currentPayload } : readPendingTouch();
  if (!pending) return;
  if (currentPayload) storePendingTouch(pending);

  const response = await fetch(resolveApiUrl("/api/member/attribution/touch"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...nativeAppRequestHeaders(),
      ...(await clientFingerprintHeaders()),
    },
    body: JSON.stringify(pending.payload),
  });
  if (!response.ok) throw new Error(`attribution touch failed: ${response.status}`);
	try {
		window.localStorage.removeItem(pendingTouchKey);
	} catch {
		// The server has already recorded the touch.
	}
}
